import { supabase } from '@/lib/supabase';
import { adminAtas, adminCategorias, adminProdutoCategorias, adminProducts } from '@/mocks/admin';

export type Ata = (typeof adminAtas)[number];
export type Produto = (typeof adminProducts)[number];
export type Categoria = (typeof adminCategorias)[number];
export type ProdutoCategoria = (typeof adminProdutoCategorias)[number];

function mapAtaFromDb(row: any): Ata {
  return {
    id: Number(row.id),
    numero: row.numero,
    nome: row.nome,
    orgao: row.orgao || '',
    uasg: row.uasg || '',
    estado: row.estado || '',
    esfera: row.esfera || '',
    objeto: row.objeto || '',
    dataInicio: row.data_inicio || '',
    dataFim: row.data_fim || '',
    dataEdital: row.data_edital || '',
    status: row.status || 'Ativa',
    visibilidade: row.visibilidade || 'Visível',
    categorias: row.categorias || [],
    anexos: row.anexos || [],
    pesquisas: row.pesquisas || 0,
  };
}

function mapAtaToDb(ata: Partial<Ata>) {
  return {
    id: ata.id,
    numero: ata.numero,
    nome: ata.nome,
    orgao: ata.orgao,
    uasg: ata.uasg,
    estado: ata.estado,
    esfera: ata.esfera,
    objeto: ata.objeto,
    data_inicio: ata.dataInicio || null,
    data_fim: ata.dataFim || null,
    data_edital: ata.dataEdital || null,
    status: ata.status,
    visibilidade: ata.visibilidade,
    categorias: ata.categorias || [],
    anexos: ata.anexos || [],
    pesquisas: ata.pesquisas || 0,
  };
}

function mapProdutoFromDb(row: any): Produto {
  const fotos = Array.isArray(row.fotos)
    ? row.fotos.map((foto: any) => ({
        ...foto,
        url: String(foto?.url || ''),
      }))
    : [];

  return {
    id: Number(row.id),
    nome: row.nome,
    sku: row.sku,
    marca: row.marca || '',
    categorias: row.categorias || [],
    estoque: row.estoque || 0,
    vendas: row.vendas || 0,
    fotos,
    variantes: row.variantes || [],
    descricao: row.descricao || '',
    atasIds: row.atas_ids || [],
    ativo: Boolean(row.ativo),
  };
}

function mapProdutoToDb(produto: Partial<Produto>) {
  return {
    id: produto.id,
    nome: produto.nome,
    sku: produto.sku,
    marca: produto.marca,
    categorias: produto.categorias || [],
    estoque: produto.estoque || 0,
    vendas: produto.vendas || 0,
    fotos: produto.fotos || [],
    variantes: produto.variantes || [],
    descricao: produto.descricao || '',
    atas_ids: produto.atasIds || [],
    ativo: produto.ativo ?? true,
  };
}

async function selectOrFallback<T>(table: string, fallback: T[], mapper: (row: any) => T, fallbackWhenEmpty = true) {
  if (!supabase) return fallback;
  const { data, error } = await supabase.from(table).select('*').order('id');
  if (error) {
    console.warn(`Falha ao carregar ${table}; usando mocks.`, error.message);
    return fallback;
  }
  if (!data || data.length === 0) return fallbackWhenEmpty ? fallback : [];
  return data.map(mapper);
}

export async function fetchAtaCategorias() {
  return selectOrFallback('donnos_ata_categorias', adminCategorias, (row) => ({
    id: Number(row.id),
    nome: row.nome,
  }));
}

export async function fetchProdutoCategorias() {
  return selectOrFallback('donnos_produto_categorias', adminProdutoCategorias, (row) => ({
    id: Number(row.id),
    nome: row.nome,
  }));
}

export async function fetchAtas() {
  return selectOrFallback('donnos_atas', adminAtas, mapAtaFromDb, false);
}

export async function fetchProdutos() {
  return selectOrFallback('donnos_produtos', adminProducts, mapProdutoFromDb, false);
}

export async function saveAta(ata: Partial<Ata>) {
  if (!supabase) return null;
  const { data, error } = await supabase.from('donnos_atas').upsert(mapAtaToDb(ata)).select().single();
  if (error) throw error;
  return mapAtaFromDb(data);
}

export async function deleteAta(id: number) {
  if (!supabase) return;
  const { error } = await supabase.from('donnos_atas').delete().eq('id', id);
  if (error) throw error;
}

export async function saveProduto(produto: Partial<Produto>) {
  if (!supabase) return null;
  const { data, error } = await supabase.from('donnos_produtos').upsert(mapProdutoToDb(produto)).select().single();
  if (error) throw error;
  return mapProdutoFromDb(data);
}

export async function deleteProduto(id: number) {
  if (!supabase) return;
  const { error } = await supabase.from('donnos_produtos').delete().eq('id', id);
  if (error) throw error;
}
